import { useEffect, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import DevelopmentMandateForm from '@/components/DevelopmentMandateForm';
import {
  EMPTY_DEVELOPMENT_MANDATE,
  normalizeMandate,
  type DevelopmentMandateSelections,
} from '@/config/developmentMandate';
import { updateProject } from '@/services/projectService';
import type { ScriptProject } from '@/types/scriptProject';

type EditProjectMandateDialogProps = {
  project: ScriptProject | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSaved: (project: ScriptProject, reassessedScripts: number) => void;
};

export default function EditProjectMandateDialog({
  project,
  open,
  onOpenChange,
  onSaved,
}: EditProjectMandateDialogProps) {
  const [mandate, setMandate] = useState<DevelopmentMandateSelections>({
    ...EMPTY_DEVELOPMENT_MANDATE,
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open && project) {
      setMandate(normalizeMandate(project.development_mandate));
      setError(null);
      setSaving(false);
    }
  }, [open, project]);

  const handleClose = (next: boolean) => {
    if (saving) return;
    onOpenChange(next);
  };

  const handleSubmit = async () => {
    if (!project) return;
    setSaving(true);
    setError(null);
    try {
      const { project: updated, reassessed_scripts } = await updateProject(project.id, {
        development_mandate: mandate,
      });
      onSaved(updated, reassessed_scripts);
      onOpenChange(false);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to save mandate');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="bg-[#141414] border-[#2a2a2a] text-white sm:max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-lg font-sans font-bold uppercase tracking-wide">
            {project ? `Edit mandate — ${project.name}` : 'Edit development mandate'}
          </DialogTitle>
          <DialogDescription className="text-white/50 font-sans text-sm mt-2">
            Mandate overrides account defaults for any section selected here. Leave sections empty
            to inherit account mandate defaults from Settings.
          </DialogDescription>
        </DialogHeader>
        <div className="ni-panel p-4">
          <DevelopmentMandateForm value={mandate} onChange={setMandate} compact />
          {error && <p className="mt-3 text-sm text-red-400 font-sans">{error}</p>}
        </div>
        <DialogFooter className="gap-2">
          <Button variant="indexOutline" onClick={() => handleClose(false)} disabled={saving}>
            Cancel
          </Button>
          <Button
            variant="indexPrimary"
            onClick={() => void handleSubmit()}
            disabled={saving || !project}
          >
            {saving ? 'Saving…' : 'Save mandate'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
